/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable tailwindcss/no-arbitrary-value */
import { Dialog, IconButton } from '@material-tailwind/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { BaseTranslations } from '../../../baseTranslations';
import { ContactMe, LeadZoho } from '../../../types/car-insurance';
import { ContactMethod } from '../../../types/car-insurance/ContactMethod';
import { MaterialIconName } from '../../../types/materialIconName';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { getUtmParams } from '../../../utils/carInsurance/getUtmParams';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { useSafeSearchParams } from '../../../utils/useSafeSearchParams';
import Button from '../../Button';
import { FeLink } from '../../FeLink';
import Close from '../../icons/Close';
import { MaterialIcon } from '../../MaterialIcon';
import { Stepper } from '../../Stepper';
import { disabledButton, nextButtonStyles } from '../constants';
import { useSafeCarInsuranceContext } from '../context/useSafeCarInsurance';
import { FormInput } from '../Form/FormInput';
import { Checkbox } from '../Inputs/Checkbox';
import { TabsIconsCheckbox } from '../Inputs/TabsCheckbox';
import { ConfirmationModal } from './ConfirmationModal';
import { ErrorModal } from './ErrorModal';
import { ContactDetailsMoneymax } from './Moneymax/ContactDetailsMoneymax';
import { VehicleDetailsMoneymax } from './Moneymax/VehicleDetailsMoneymax';
import { SelectContactMethod } from './SelectContactMethod';
import { ContactDetailsSingsaver } from './Singsaver/ContactDetailsSingsaver';
import { PersonalDetailsSingsaver } from './Singsaver/PersonalDetailsSingsaver';
import { VehicleDetailsSingsaver } from './Singsaver/VehicleDetailsSingsaver';
import { useCentralModals } from './useSuccessErrorModal';

export type CentralModalCIProps = {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;

  onContactMe: (leadData: LeadZoho) => Promise<{ status: boolean }>;
  pushDataLayer: PushDataLayerFn;
  contactMe?: ContactMe;
};

export const CentralModalCI: React.FC<CentralModalCIProps> = ({
  isOpen,
  onClose,
  onOpen,
  onContactMe,
  pushDataLayer,
  contactMe,
}) => {
  const {
    openErrorModal,
    openConfirmationModal,
    isErrorOpen,
    isConfirmationOpen,
    closeConfirmationModal,
    closeErrorModal,
  } = useCentralModals();
  const [activeStep, setActiveStep] = useState(0);
  const searchParams = useSafeSearchParams();
  const context = useCmsClientContext();
  const { brand } = context;
  const staticText = getClientTranslations(BaseTranslations, context);
  const isMoneymax = brand === 'moneymax';

  const carInsuranceContext = useSafeCarInsuranceContext();

  const valueUser = carInsuranceContext?.requestParams?.user?.value;
  const setValueUser = carInsuranceContext?.requestParams?.user?.setValue;

  useEffect(() => {
    if (!setValueUser || !valueUser) {
      return;
    }

    if (isOpen && !valueUser.preferredContactMethod) {
      setValueUser({
        ...valueUser,
        preferredContactMethod: ContactMethod.WA,
      });
    }
  }, [isOpen, valueUser?.preferredContactMethod, valueUser, setValueUser]);

  if (!carInsuranceContext || !valueUser) {
    return null;
  }

  const {
    requestParams: {
      vehicle: { value: valueVehicle, setValue: setValueVehicle },
      prices: {
        value: { personalAccident, propertyDamage },
      },
    },
    form,
  } = carInsuranceContext;

  const { preferredContactMethod, phone, email, firstName, lastName } =
    valueUser;
  const {
    consentDirectMarketing,
    makeName,
    modelCode,
    trim,
    type,
    manufactureYear,
    fairMarket,
  } = valueVehicle;

  const waContact = contactMe ?? defaultContactMe;
  const createWALink = (
    message: string = waContact.whatsapp.message_template_empty,
  ): string => {
    const waNumber = waContact.whatsapp.number.trim();
    if (!waNumber) return '';
    const encodedMsg = encodeURIComponent(message);
    return `https://wa.me/${waNumber}?text=${encodedMsg}`;
  };

  const onContactMeHandler = async () => {
    pushDataLayer({
      event: 'form_interact',
      lead_form_name: 'ci-rp-contact-us-form',
      form_type: 'lead form',
      action: 'click',
      component_type: 'button',
      component_title: 'Contact Me',
      vertical_code: 'CI',
    });

    try {
      const leadMoneymaxData: LeadZoho = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: phone,
        car_brand: makeName,
        car_model: modelCode,
        car_year: manufactureYear.toString(),
        choose_car_trim: trim,
        vehicle_type: type,
        acts_of_nature_coverage: true,
        car_fmv: fairMarket.toString(),
        searchParams: getUtmParams(searchParams),
        bodily: propertyDamage.toString(),
        property_damage: propertyDamage.toString(),
        personal_acc: personalAccident.toString(),
      };

      const res = await onContactMe(leadMoneymaxData);

      pushDataLayer({
        event: 'form_submit',
        lead_form_name: 'ci-rp-contact-us-form',
        form_type: 'lead form',
        vertical_code: 'CI',
      });

      if (res.status) {
        onClose();
        openConfirmationModal();
      } else {
        onClose();
        openErrorModal();
        return false;
      }
    } catch (error) {
      pushDataLayer({
        event: 'form_error',
        lead_form_name: 'ci-rp-contact-us-form',
        form_type: 'lead form',
        vertical_code: 'CI',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
      openErrorModal();
      onClose();
    }
  };

  const updateContact = (updated: Partial<typeof valueUser>) => {
    setValueUser!({
      ...valueUser,
      ...updated,
    });
  };

  const updateVehicle = (updated: Partial<typeof valueVehicle>) => {
    setValueVehicle({
      ...valueUser,
      ...updated,
    });
  };

  const renderVehiicleDetail = () => {
    if (isMoneymax) {
      return (
        <VehicleDetailsMoneymax
          onNext={() => setActiveStep(2)}
          pushDataLayer={pushDataLayer}
        />
      );
    } else {
      return (
        <VehicleDetailsSingsaver
          onNext={() => setActiveStep(1)}
          pushDataLayer={pushDataLayer}
        />
      );
    }
  };

  const renderPersonDetails = () => {
    if (!isMoneymax) {
      return (
        <PersonalDetailsSingsaver
          onNext={() => setActiveStep(2)}
          pushDataLayer={pushDataLayer}
        />
      );
    }
  };

  const renderContactDetails = () => {
    if (isMoneymax) {
      return (
        <ContactDetailsMoneymax
          onBackword={() => setActiveStep(0)}
          pushDataLayer={pushDataLayer}
        />
      );
    } else {
      return <ContactDetailsSingsaver />;
    }
  };

  const renderContactMethod = () => {
    const validEmailContactMethod =
      preferredContactMethod === ContactMethod.Email && Boolean(email?.trim());

    const validPhoneContactMethod =
      (preferredContactMethod === ContactMethod.WA ||
        preferredContactMethod === ContactMethod.Phone) &&
      Boolean(phone?.trim());

    const contactButton =
      Boolean(firstName?.trim()) &&
      Boolean(lastName?.trim()) &&
      (validEmailContactMethod || validPhoneContactMethod);

    if (!isMoneymax)
      return (
        <div className="mb-2 mt-3">
          {activeStep !== 2 && (
            <div className="text-center">
              <p className="font-base font-medium text-gray-700 text-base">
                {staticText.or}
              </p>
            </div>
          )}

          <TabsIconsCheckbox
            options={contactMethodIconOptions}
            onChange={(val: string) =>
              updateContact({
                preferredContactMethod: val as ContactMethod,
              })
            }
            value={preferredContactMethod ?? ContactMethod.WA}
            label={staticText.preferred_contact_method}
          />

          {activeStep === 2 && (
            <>
              {(preferredContactMethod === ContactMethod.WA ||
                preferredContactMethod === ContactMethod.Phone ||
                !preferredContactMethod) && (
                <div className="mt-4">
                  <FormInput
                    label={staticText.phone_number}
                    value={phone}
                    placeholder={staticText.phone_number}
                    type="text"
                    onChange={(value) => updateContact({ phone: value })}
                  />
                </div>
              )}

              {preferredContactMethod === ContactMethod.Email && (
                <div className="mt-4">
                  <FormInput
                    label={staticText.email}
                    value={email}
                    placeholder={staticText.email}
                    type="text"
                    onChange={(value) => updateContact({ email: value })}
                  />
                </div>
              )}

              <div className="my-4 flex flex-col gap-4">
                <div className="flex gap-2">
                  <div className="w-5">
                    <Checkbox
                      onClick={() =>
                        updateVehicle({
                          consentDirectMarketing: !consentDirectMarketing,
                        })
                      }
                      containerClass="!size-5"
                      defaultValue={consentDirectMarketing}
                    />
                  </div>
                  <span className="grow font-medium text-gray-900 text-sm">
                    {staticText.marketing}
                  </span>
                </div>

                <Button
                  className={classNames(
                    contactButton ? nextButtonStyles : disabledButton,
                  )}
                  disabled={!contactButton}
                  onClick={onContactMeHandler}
                >
                  {staticText.contact_me}
                </Button>

                <div className="text-center">
                  <p className="font-base font-medium text-gray-700 text-base">
                    {staticText.or}
                  </p>
                </div>
              </div>
            </>
          )}

          <SelectContactMethod
            contactMethod={preferredContactMethod ?? ''}
            createWALink={createWALink}
            stepNumber={activeStep + 1}
            contactInfo={waContact}
            pushDataLayer={pushDataLayer}
          />
        </div>
      );
  };

  const handleErrorModalTryAgainClick = () => {
    openErrorModal();
    onOpen();
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return renderVehiicleDetail();
      case 1:
        return renderPersonDetails();
      case 2:
        return renderContactDetails();
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        handler={onClose}
        className={classNames(
          'max-h-[90vh] overflow-y-auto md:!max-w-screen-2sm lg:!max-w-screen-2sm',
          {
            'lg:!min-w-[450px]': isMoneymax,
            'lg:!min-w-[550px] flex gap-4 justify-between p-4': !isMoneymax,
          },
        )}
      >
        {activeStep > 0 && !isMoneymax ? (
          <IconButton
            variant="text"
            onClick={() => setActiveStep(activeStep - (!isMoneymax ? 1 : 2))}
            className="!size-6"
          >
            <MaterialIcon
              icon="arrow_left_alt"
              className="flex text-gray-900"
            />
          </IconButton>
        ) : (
          <div></div>
        )}
        <div
          className={classNames({
            'h-full size-full': isMoneymax,
            'w-[420px]': !isMoneymax,
          })}
        >
          <div className="flex w-full shrink-0 flex-col p-4">
            <div className="flex w-full items-center justify-center">
              <Stepper
                steps={!isMoneymax ? STEPS : STEPS.filter((_, i) => i !== 1)}
                activeStep={activeStep}
              />
            </div>
            {renderStep()}
            {renderContactMethod()}
          </div>
          <div className="flex w-full items-center justify-center rounded-b-md border border-t-[#E0E0E0] p-4">
            <span className="text-center font-medium text-gray-700 text-xs md:text-sm lg:text-base">
              {staticText.by_continuing}{' '}
              {brand.charAt(0).toUpperCase() + brand.slice(1)}{' '}
              <FeLink
                href={termCondition[brand]}
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                {staticText.terms_and_conditions}
              </FeLink>{' '}
              {staticText.and}{' '}
              <FeLink
                href={privacyPolicy[brand]}
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                {staticText.privacy_and_policy}
              </FeLink>
            </span>
          </div>
        </div>
        {!isMoneymax && (
          <IconButton variant="text" onClick={onClose} className="!size-6">
            <Close className="flex size-6 text-gray-900" />
          </IconButton>
        )}
      </Dialog>
      <ConfirmationModal
        isOpen={isConfirmationOpen && !isMoneymax}
        onClose={() => {
          closeConfirmationModal();
          if (['singsaver', 'moneyhero'].includes(brand)) {
            form.setIsChanged(true);
          }
        }}
        waLink={createWALink()}
        pushDataLayer={pushDataLayer}
      />
      <ErrorModal
        isOpen={isErrorOpen}
        onClose={() => {
          closeErrorModal();
          if (['singsaver', 'moneyhero'].includes(brand)) {
            form.setIsChanged(true);
          }
        }}
        onTryAgain={handleErrorModalTryAgainClick}
      />
    </>
  );
};

const STEPS: { icon: MaterialIconName }[] = [
  { icon: 'directions_car' },
  { icon: 'account_circle' },
  { icon: 'call' },
];

const contactMethodIconOptions = [
  { label: 'whatsapp', value: ContactMethod.WA },
  { label: 'phone', value: ContactMethod.Phone },
  { label: 'email', value: ContactMethod.Email },
];

const privacyPolicy = {
  singsaver: 'https://www.singsaver.com.sg/page/singsaver-privacy-policy',
  moneymax: 'https://www.moneymax.ph/page/privacy-policy',
  money101: '',
  moneyhero: 'https://www.moneyherogroup.com/privacy-policy',
};

const termCondition = {
  singsaver: 'https://www.singsaver.com.sg/page/singsaver-terms',
  moneymax: 'https://www.moneymax.ph/page/terms-and-conditions',
  money101: '',
  moneyhero: 'https://www.moneyherogroup.com/terms-conditions',
};

export const defaultContactMe: ContactMe = {
  email: {
    email: '',
    caption: '',
    note: '',
  },
  phone: {
    number: '',
    caption: '',
    note: '',
  },
  whatsapp: {
    number: '',
    caption: '',
    message_template: '',
    message_template_empty: '',
  },
  show: false,
  title: '',
};
